import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../config/api';
import { PageLayout, ActionButton } from '../../components/DesignSystem';
import { Button, message, Tag, Select, Input, Table } from 'antd';
import { apiFetch } from '../../utils/apiFetch';

const SupplierView: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [supplier, setSupplier] = useState<any>(null);
  const [supplierLogo, setSupplierLogo] = useState('');
  const [attachments, setAttachments] = useState<any[]>([]);
  const [costHistory, setCostHistory] = useState<any[]>([]);
  const [loading, setLoading]   = useState(true);
  const { token } = useAuth();

  // Purchase modal state
  const [showPurchase, setShowPurchase] = useState(false);
  const [submittingPurchase, setSubmittingPurchase] = useState(false);
  const [purchaseHistory, setPurchaseHistory] = useState<any[]>([]);
  const [purchaseHistoryLoading, setPurchaseHistoryLoading] = useState(false);
  const [purchase, setPurchase] = useState({
    category: 'Stock & Inventory',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    reference: '',
    notes: ''
  });

  useEffect(() => { fetchSupplier(); }, [id, token]);
  useEffect(() => {
    if (supplier && token) {
      fetchAttachments();
      fetchCostHistory();
      fetchPurchaseHistory();
      if (supplier.has_logo || supplier.hasLogo) fetchSupplierLogo();
      else setSupplierLogo('');
    }
  }, [supplier?.id, token]);

  const fetchSupplierLogo = async () => {
    try {
      if (!token || !id) return;
      const res = await apiFetch(`${API_URL}/suppliers/${id}/logo`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setSupplierLogo(data.image || '');
      }
    } catch { /* optional */ }
  };

  // Auto-open the purchase modal if navigated here with state.openPurchase=true
  useEffect(() => {
    if (supplier && (location.state as any)?.openPurchase) {
      setShowPurchase(true);
      // Clear state so refreshing the page doesn't re-open it
      window.history.replaceState({}, '');
    }
  }, [supplier, location.state]);

  const fetchPurchaseHistory = async () => {
    const supplierId = String(supplier?.id || supplier?._id || id || '');
    if (!supplierId || !token) return;
    setPurchaseHistoryLoading(true);
    try {
      // PO receipts + stock movements first (supplier-scoped, fast)
      const [poRes, movRes] = await Promise.all([
        apiFetch(`${API_URL}/purchase-orders?supplier_id=${supplierId}`, { headers: { Authorization: `Bearer ${token}` } }),
        apiFetch(`${API_URL}/stock-movements?supplier_id=${supplierId}&limit=100`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      const poEntries: any[] = [];
      if (poRes.ok) {
        const poList = await poRes.json();
        poList
          .filter((po: any) => po.status === 'Received' || po.status === 'Partial')
          .forEach((po: any) => poEntries.push({
            id: `po-${po.id}`,
            date: po.updated_at || po.created_at,
            category: 'Purchase Order',
            amount: parseFloat(po.total || 0),
            note: `PO ${po.po_number} — ${po.status}`,
            _source: 'po',
          }));
      }

      const movEntries: any[] = [];
      if (movRes.ok) {
        const movList = await movRes.json();
        (Array.isArray(movList) ? movList : []).forEach((m: any) => movEntries.push({
          id: `mov-${m.id}`,
          date: m.created_at,
          category: m.product_name || 'Stock',
          amount: null,
          note: m.reference || m.note || null,
          _source: 'movement',
          _movType: m.type,
          _qty: parseFloat(m.quantity || 0),
        }));
      }

      const fastCombined = [...poEntries, ...movEntries]
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setPurchaseHistory(fastCombined);

      // Manual accounting expenses — loaded in background (full ledger is heavy)
      const accRes = await apiFetch(`${API_URL}/accounting?period=1y&type=expense`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const accEntries: any[] = [];
      if (accRes.ok) {
        const data = await accRes.json();
        const raw = data.entries || [];
        raw
          .filter((e: any) => e.supplier_id && String(e.supplier_id) === supplierId)
          .forEach((e: any) => accEntries.push({ ...e, _source: 'manual' }));
      }

      if (accEntries.length > 0) {
        const combined = [...accEntries, ...poEntries, ...movEntries]
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        setPurchaseHistory(combined);
      }
    } catch { /* non-blocking */ }
    finally { setPurchaseHistoryLoading(false); }
  };

  const fetchAttachments = async () => {
    try {
      const supplierId = supplier?.id || supplier?._id || id;
      if (!supplierId || !token) return;
      const res = await apiFetch(`${API_URL}/suppliers/${supplierId}/attachments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setAttachments(await res.json());
    } catch { /* ignore */ }
  };

  const fetchCostHistory = async () => {
    try {
      const supplierId = supplier?.id || supplier?._id || id;
      if (!supplierId || !token) return;
      const res = await apiFetch(`${API_URL}/suppliers/${supplierId}/cost-history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setCostHistory(await res.json());
    } catch { /* ignore */ }
  };

  const handleRecordPurchase = async () => {
    if (!purchase.amount || parseFloat(purchase.amount) <= 0) {
      message.error('Please enter a valid amount');
      return;
    }
    setSubmittingPurchase(true);
    try {
      const res = await apiFetch(`${API_URL}/accounting`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          type: 'expense',
          category: purchase.category,
          amount: parseFloat(purchase.amount),
          date: purchase.date,
          note: `${supplier.name}${purchase.reference ? ` — Ref: ${purchase.reference}` : ''}${purchase.notes ? ` — ${purchase.notes}` : ''}`,
          supplier_id: supplier.id
        })
      });
      if (res.ok) {
        message.success(`✅ Purchase of £${parseFloat(purchase.amount).toFixed(2)} from ${supplier.name} added to Accounting`);
        setPurchase({ category: 'Stock & Inventory', amount: '', date: new Date().toISOString().split('T')[0], reference: '', notes: '' });
        setShowPurchase(false);
        fetchPurchaseHistory();
      } else {
        const err = await res.json();
        message.error(err.error || 'Failed to record purchase');
      }
    } catch {
      message.error('Network error');
    } finally {
      setSubmittingPurchase(false);
    }
  };

  const fetchSupplier = async () => {
    try {
      if (!token) { navigate('/login'); return; }
      const res = await apiFetch(`${API_URL}/suppliers/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Not found');
      const data = await res.json();
      if (data && !data.id && data._id) data.id = data._id;
      setSupplier(data);
    } catch {
      message.error('Failed to load supplier');
      navigate('/suppliers/list');
    } finally {
      setLoading(false);
    }
  };

  const archiveSupplier = async () => {
    const reason = window.prompt(`Archive reason for "${supplier?.name}"?`);
    if (!reason) return;
    try {
      if (!token) { navigate('/login'); return; }
      const res = await apiFetch(`${API_URL}/suppliers/${id}/archive`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      });
      if (res.ok) { message.success('Supplier archived'); navigate('/suppliers/list'); }
      else { const e = await res.json(); message.error(e.message || 'Archive failed'); }
    } catch { message.error('Network error'); }
  };

  if (loading) return <p style={{ padding: 40, textAlign: 'center' }}>⏳ Loading...</p>;
  if (!supplier) return <p style={{ padding: 40, textAlign: 'center' }}>❌ Supplier not found</p>;

  const get = (snake: string, camel?: string) =>
    supplier[snake] ?? (camel ? supplier[camel] : undefined);

  const fmt = (v: any) => v ? new Date(v).toLocaleString('en-GB') : 'N/A';

  const statusColor =
    supplier.status === 'Active'   ? '#52c41a' :
    supplier.status === 'Inactive' ? '#faad14' :
    supplier.status === 'Blocked'  ? '#ff4d4f' : '#8c8c8c';

  const imageUrl = supplierLogo || supplier.image || supplier.logo || '';
  const resolvedImageUrl = imageUrl
    ? (imageUrl.startsWith('data:') || imageUrl.startsWith('http://') || imageUrl.startsWith('https://') || imageUrl.startsWith('//')
      ? imageUrl
      : `${API_URL.replace('/api', '')}${imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`}`)
    : '';
  const initial          = (supplier.name || '?')[0].toUpperCase();
  const isPreferred      = !!(supplier.preferredSupplier || supplier.preferred_supplier);
  const bankSortCode     = supplier.bankSortCode     || supplier.bank_sort_code     || '';
  const bankAccountNumber= supplier.bankAccountNumber|| supplier.bank_account_number|| '';
  const reorderThreshold = supplier.reorderThreshold || supplier.reorder_threshold  || 0;
  const leadTimeDays     = supplier.leadTimeDays || supplier.lead_time_days || supplier.leadTime || supplier.lead_time || 0;
  const contactPerson    = get('contactPerson', 'contact_person');
  const paymentTerms     = get('paymentTerms',  'payment_terms') || 'Net 30';
  const taxId            = get('taxId',         'tax_id');

  return (
    <PageLayout
      title={`🏭 ${supplier.name}`}
      action={
        <div style={{ display: 'flex', gap: 10 }}>
          <Button
            type="primary"
            style={{ background: '#2563eb', borderColor: '#2563eb', fontWeight: 600 }}
            onClick={() => navigate(`/purchase-orders?supplier_id=${id}`)}
          >
            📋 New PO
          </Button>
          <Button
            type="primary"
            style={{ background: '#16a34a', borderColor: '#16a34a', fontWeight: 600 }}
            onClick={() => setShowPurchase(true)}
          >
            💳 Record Purchase
          </Button>
          <ActionButton variant="edit" label="Edit" onClick={() => navigate(`/suppliers/edit/${id}`)} />
          <Button danger onClick={archiveSupplier}>Archive</Button>
          <Button onClick={() => navigate('/suppliers/list')}>← Back</Button>
        </div>
      }
    >
      <div style={s.card}>
        {/* ── Status / Identity Banner ── */}
        <div style={{ ...s.banner, background: statusColor }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {resolvedImageUrl ? (
              <img src={resolvedImageUrl} alt={supplier.name} style={s.bannerAvatar} />
            ) : (
              <div style={s.bannerAvatarFallback}>{initial}</div>
            )}
            <div>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 20 }}>{supplier.name}</div>
              {supplier.company && <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 14 }}>{supplier.company}</div>}
              {isPreferred && (
                <span style={{ display: 'inline-block', marginTop: 4, background: 'rgba(255,255,255,0.25)', borderRadius: 12, padding: '2px 10px', fontSize: 12, color: '#fff', fontWeight: 600 }}>
                  ⭐ Preferred Supplier
                </span>
              )}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <span style={s.statusPill}>{supplier.status || 'Active'}</span>

          </div>
        </div>

        {/* ── Basic Information ── */}
        <div style={s.section}>
          <h3 style={s.sectionTitle}>Basic Information</h3>

          {/* Logo displayed in view */}
          {resolvedImageUrl && (
            <div style={{ marginBottom: 20 }}>
              <div style={s.fieldLabel}>Logo / Photo</div>
              <img src={resolvedImageUrl} alt="supplier logo" style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '2px solid #eee', marginTop: 4 }} />
            </div>
          )}

          <div style={s.grid}>
            <Field label="Supplier Name"   value={supplier.name} />
            <Field label="Company"         value={supplier.company} />
            <Field label="Contact Person"  value={contactPerson} />
            <Field label="Category"        value={supplier.category} />
            <Field label="Email"           value={supplier.email}
              link={supplier.email ? `mailto:${supplier.email}` : undefined} />
            <Field label="Phone"           value={supplier.phone}
              link={supplier.phone ? `tel:${supplier.phone}` : undefined} />
            <Field label="Preferred Supplier" value={isPreferred ? 'Yes ⭐' : 'No'} highlight={isPreferred ? '#52c41a' : undefined} />
          </div>
        </div>

        {/* ── Address Information ── */}
        <div style={s.section}>
          <h3 style={s.sectionTitle}>Address Information</h3>
          <div style={s.grid}>
            <Field label="Address" value={supplier.address} />
            <Field label="City"    value={supplier.city} />
            <Field label="Postcode" value={supplier.postcode} />
            <Field label="Country" value={supplier.country} />
          </div>
        </div>

        {/* ── Business Details ── */}
        <div style={s.section}>
          <h3 style={s.sectionTitle}>Business Details</h3>
          <div style={s.grid}>
            <Field label="Website"          value={supplier.website}
              link={supplier.website ? supplier.website : undefined} />
            <Field label="Tax ID / VAT Number" value={taxId} />
            <Field label="Bank Sort Code"      value={bankSortCode} />
            <Field label="Bank Account Number" value={bankAccountNumber} />
            <Field label="Payment Terms"       value={paymentTerms} />
            <Field label="Lead Time"           value={leadTimeDays ? `${leadTimeDays} days` : undefined} />
            <Field label="Reorder Threshold"   value={reorderThreshold ? `${reorderThreshold} units` : undefined} />
            <Field label="Status"              value={supplier.status || 'Active'} highlight={statusColor} />
          </div>
        </div>

        {/* ── Additional Notes ── */}
        {supplier.notes && (
          <div style={s.section}>
            <h3 style={s.sectionTitle}>Additional Notes</h3>
            <div style={s.notesBox}>{supplier.notes}</div>
          </div>
        )}

        {/* ── Attachments ── */}
        <div style={s.section}>
          <h3 style={s.sectionTitle}>📎 Attachments</h3>
          {attachments.length > 0 ? (
            <ul style={{ paddingLeft: 20, margin: 0 }}>
              {attachments.map((att: any) => (
                <li key={att.id} style={{ marginBottom: 6 }}>
                  <a href={att.url} target="_blank" rel="noopener noreferrer">{att.filename}</a>
                </li>
              ))}
            </ul>
          ) : (
            <span style={{ fontSize: 13, color: '#888' }}>No attachments yet — add price lists or contracts when editing this supplier.</span>
          )}
        </div>

        {/* ── Product Cost History (from POs) ── */}
        <div style={s.section}>
          <h3 style={s.sectionTitle}>📊 Product Cost History</h3>
          <p style={{ fontSize: 13, color: '#6b7280', marginTop: -8, marginBottom: 12 }}>
            Unit costs from purchase orders — use when negotiating or reviewing margin.
          </p>
          <Table
            size="small"
            rowKey={(row, i) => `${row.product_id}-${row.po_number}-${i}`}
            dataSource={costHistory}
            locale={{ emptyText: 'No purchase order cost data yet — create and receive POs to build history.' }}
            pagination={{
              pageSize: 8,
              showSizeChanger: true,
              pageSizeOptions: ['8', '15', '25'],
              showTotal: (total) => `${total} cost record${total !== 1 ? 's' : ''}`,
              hideOnSinglePage: true,
            }}
            columns={[
              { title: 'Product', dataIndex: 'product_name', key: 'product_name', ellipsis: true },
              { title: 'SKU', dataIndex: 'sku', key: 'sku', width: 90, render: (v: string) => v || '—' },
              {
                title: 'Unit Cost', dataIndex: 'unit_cost', key: 'unit_cost', width: 100, align: 'right' as const,
                render: (v: number) => <span style={{ fontWeight: 600 }}>£{Number(v || 0).toFixed(2)}</span>,
              },
              { title: 'Qty Ordered', dataIndex: 'quantity_ordered', key: 'quantity_ordered', width: 100, align: 'center' as const },
              { title: 'Received', dataIndex: 'quantity_received', key: 'quantity_received', width: 90, align: 'center' as const, render: (v: number) => v || 0 },
              { title: 'PO', dataIndex: 'po_number', key: 'po_number', width: 90, render: (v: string) => v || '—' },
              {
                title: 'Last Received', key: 'last_received_at', width: 120, align: 'center' as const,
                render: (_: unknown, row: any) => {
                  const d = row.last_received_at || row.order_date;
                  return d ? new Date(d).toLocaleDateString('en-GB') : '—';
                },
              },
            ]}
          />
        </div>

        {/* ── Record Info ── */}
        <div style={s.section}>
          <h3 style={s.sectionTitle}>Record Information</h3>
          <div style={s.grid}>
            <Field label="Supplier ID"  value={supplier.id} />
            <Field label="Created"      value={fmt(supplier.createdAt || supplier.created_at)} />
            {(supplier.updatedAt || supplier.updated_at) && (
              <Field label="Last Updated" value={fmt(supplier.updatedAt || supplier.updated_at)} />
            )}
            {supplier.deleted_at && (
              <Field label="Archived On" value={fmt(supplier.deleted_at)} highlight="#ff4d4f" />
            )}
          </div>
        </div>
      </div>

      {/* ── Purchase History ── */}
      <div style={{ ...s.card, marginTop: 20 }}>
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
            💸 Purchase History (Last 12 Months)
            {purchaseHistoryLoading && <span style={{ fontSize: 12, fontWeight: 500, color: '#9ca3af', marginLeft: 8 }}>Updating…</span>}
          </h3>
          <Button type="primary" size="small" style={{ background: '#16a34a', borderColor: '#16a34a' }} onClick={() => setShowPurchase(true)}>
            + Record Purchase
          </Button>
        </div>
        <div style={{ padding: '0 8px 8px' }}>
          <Table
            size="small"
            rowKey="id"
            dataSource={purchaseHistory}
            locale={{ emptyText: 'No purchases recorded for this supplier yet.' }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50'],
              showTotal: (total) => `${total} purchase${total !== 1 ? 's' : ''}`,
              hideOnSinglePage: true,
            }}
            columns={[
              {
                title: 'Date', dataIndex: 'date', key: 'date', width: 110,
                render: (d: string) => new Date(d).toLocaleDateString('en-GB'),
              },
              {
                title: 'Type', key: 'type', width: 130,
                render: (_: unknown, e: any) => (
                  e._source === 'po'
                    ? <Tag color="blue">PO Receipt</Tag>
                    : e._source === 'movement'
                      ? e._movType === 'stock_in'
                        ? <Tag color="green">Stock In</Tag>
                        : e._movType === 'stock_out'
                          ? <Tag color="red">Stock Out</Tag>
                          : <Tag color="gold">Adjustment</Tag>
                      : <Tag color="cyan">Manual</Tag>
                ),
              },
              { title: 'Category', dataIndex: 'category', key: 'category', ellipsis: true },
              {
                title: 'Amount', key: 'amount', width: 120, align: 'right' as const,
                render: (_: unknown, e: any) => (
                  e._source === 'movement'
                    ? (e._qty > 0
                      ? <span style={{ color: '#15803d', fontWeight: 600 }}>+{e._qty} units</span>
                      : <span style={{ color: '#b91c1c', fontWeight: 600 }}>{e._qty} units</span>)
                    : e.amount != null
                      ? <span style={{ fontWeight: 700, color: '#dc2626' }}>£{Number(e.amount).toFixed(2)}</span>
                      : '—'
                ),
              },
              {
                title: 'Note', key: 'note', ellipsis: true,
                render: (_: unknown, e: any) => e.note || e.notes || '—',
              },
            ]}
          />
        </div>
      </div>

      {/* ── Record Purchase Modal ── */}
      {showPurchase && (
        <div
          onClick={() => setShowPurchase(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 12, padding: 32, width: 480, maxWidth: '95vw', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}
          >
            <div style={{ background: '#16a34a', borderRadius: 8, padding: '14px 20px', marginBottom: 24 }}>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 18 }}>💳 Record Purchase</div>
              <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, marginTop: 2 }}>from {supplier.name}</div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={s.formLabel}>Category *</label>
                <Select
                  value={purchase.category}
                  onChange={v => setPurchase(p => ({ ...p, category: v }))}
                  style={{ width: '100%' }}
                >
                  <Select.Option value="Stock & Inventory">📦 Stock &amp; Inventory</Select.Option>
                  <Select.Option value="Equipment">🔧 Equipment</Select.Option>
                  <Select.Option value="Software & Subscriptions">💻 Software &amp; Subscriptions</Select.Option>
                  <Select.Option value="Professional Fees">💼 Professional Fees</Select.Option>
                  <Select.Option value="Marketing">📣 Marketing</Select.Option>
                  <Select.Option value="Office Supplies">📦 Office Supplies</Select.Option>
                  <Select.Option value="Utilities">💡 Utilities</Select.Option>
                  <Select.Option value="Other Expense">💸 Other Expense</Select.Option>
                </Select>
              </div>
              <div>
                <label style={s.formLabel}>Amount (£) *</label>
                <Input
                  type="number" placeholder="0.00" step="0.01" min="0"
                  value={purchase.amount}
                  onChange={e => setPurchase(p => ({ ...p, amount: e.target.value }))}
                />
              </div>
              <div>
                <label style={s.formLabel}>Date</label>
                <Input
                  type="date"
                  value={purchase.date}
                  onChange={e => setPurchase(p => ({ ...p, date: e.target.value }))}
                />
              </div>
              <div>
                <label style={s.formLabel}>Reference / Invoice #</label>
                <Input
                  placeholder="e.g. INV-2026-001"
                  value={purchase.reference}
                  onChange={e => setPurchase(p => ({ ...p, reference: e.target.value }))}
                />
              </div>
              <div>
                <label style={s.formLabel}>Notes (optional)</label>
                <Input
                  placeholder="Additional details..."
                  value={purchase.notes}
                  onChange={e => setPurchase(p => ({ ...p, notes: e.target.value }))}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
              <Button onClick={() => setShowPurchase(false)}>Cancel</Button>
              <Button
                type="primary"
                loading={submittingPurchase}
                style={{ background: '#16a34a', borderColor: '#16a34a' }}
                onClick={handleRecordPurchase}
              >
                Record Expense
              </Button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

const Field = ({ label, value, highlight, link }: { label: string; value: any; highlight?: string; link?: string }) => (
  <div style={{ marginBottom: 18 }}>
    <span style={{ display: 'block', fontSize: 11, color: '#6c757d', fontWeight: 700, marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
      {label}
    </span>
    {link ? (
      <a href={link} target="_blank" rel="noreferrer" style={{ fontSize: 15, color: '#0074D9', fontWeight: 500, textDecoration: 'none' }}>
        {value || '—'}
      </a>
    ) : (
      <span style={{ fontSize: 15, color: highlight || '#212529', fontWeight: highlight ? 700 : 500 }}>
        {value || '—'}
      </span>
    )}
  </div>
);

const s: any = {
  card: { background: '#fff', borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.09)', overflow: 'hidden', marginBottom: 30 },

  banner: { padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 },
  bannerAvatar: { width: 56, height: 56, borderRadius: '50%', objectFit: 'cover' as const, border: '2px solid rgba(255,255,255,0.4)' },
  bannerAvatarFallback: {
    width: 56, height: 56, borderRadius: '50%',
    background: 'rgba(255,255,255,0.25)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#fff', fontWeight: 700, fontSize: 24, flexShrink: 0
  },
  statusPill: {
    display: 'inline-block', padding: '4px 16px',
    background: 'rgba(255,255,255,0.25)', borderRadius: 20,
    color: '#fff', fontWeight: 700, fontSize: 13
  },

  section: { padding: '20px 24px', borderTop: '1px solid #eee' },
  sectionTitle: { marginTop: 0, marginBottom: 14, color: '#1a1a2e', fontSize: 16, fontWeight: 700, letterSpacing: '-0.2px' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0 28px' },

  fieldLabel: { fontSize: 11, color: '#6c757d', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 4 },
  notesBox: { background: '#f8f9fa', padding: '12px 16px', borderRadius: 6, fontSize: 14, color: '#495057', lineHeight: 1.6, whiteSpace: 'pre-wrap' },
  th: { padding: '10px 16px', textAlign: 'left' as const, fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' as const, letterSpacing: '0.5px', borderBottom: '1px solid #f0f0f0' },
  td: { padding: '12px 16px', fontSize: 14, color: '#1f2937' },
  formLabel: { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 },
};

export default SupplierView;
